import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../api/client.js';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState(null);
  const authCheckInProgress = useRef(false);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Set up automatic token refresh
  useEffect(() => {
    if (isAuthenticated) {
      const refreshInterval = setInterval(async () => {
        try {
          await authAPI.refreshToken();
        } catch (error) {
          console.error('Auto refresh failed:', error);
          logout();
        }
      }, 23 * 60 * 60 * 1000); // Refresh every 23 hours

      return () => clearInterval(refreshInterval);
    }
  }, [isAuthenticated]);

  const checkAuth = useCallback(async () => {
    // Prevent multiple simultaneous auth checks
    if (authCheckInProgress.current) {
      return;
    }
    
    authCheckInProgress.current = true;
    
    try {
      setError(null);
      if (authAPI.isAuthenticated()) {
        const userData = await authAPI.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setError(error.message);
      // Clear invalid token
      authAPI.clear();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
      authCheckInProgress.current = false;
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      console.log('AuthContext: Starting login...');
      setError(null);
      setIsLoading(true);
      const response = await authAPI.login(email, password);
      console.log('AuthContext: Login API response:', response);
      setUser(response.user);
      setIsAuthenticated(true);
      console.log('AuthContext: Login state updated - user:', response.user, 'isAuthenticated: true');
      return response;
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
      console.log('AuthContext: Login loading set to false');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await authAPI.register(userData);
      setUser(response.user);
      setIsAuthenticated(true);
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      setError(null);
      await authAPI.refreshToken();
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Refresh auth failed:', error);
      setError(error.message);
      logout();
    }
  }, [logout]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    register,
    checkAuth,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
